import { useState } from 'react';
import { Table, TableBody, TableHead, TableRow, IconButton, Chip, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  NoDataSkeleton,
  ConfirmationDialog,
} from '../../../../../common/components';
import { SEVERITY_LABELS, SEVERITY_COLORS, PHASE_LABELS } from '../../constants/tasksConstants';

/**
 * Componente de tabla para las tareas de una plantilla
 */
const TasksDataTable = ({ tasks, onEdit, onDelete, templateType }) => {
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    taskId: null,
    taskInfo: null,
  });

  const isQualityTemplate = templateType === 'quality';

  const handleDeleteClick = (task) => {
    setDeleteDialog({
      open: true,
      taskId: task.uuid,
      taskInfo: task,
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      taskId: null,
      taskInfo: null,
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.taskId) {
      onDelete(deleteDialog.taskId);
      handleDeleteCancel();
    }
  };

  const getColumnWidths = () => {
    if (isQualityTemplate) {
      return {
        order: '5%',
        name: '20%',
        description: '25%',
        phase: '10%',
        maxPercentage: '8%',
        defaultPercentage: '8%',
        severity: '9%',
        actions: '5%',
      };
    } else {
      return {
        order: '5%',
        name: '30%',
        description: '35%',
        maxPercentage: '10%',
        defaultPercentage: '10%',
        actions: '10%',
      };
    }
  };

  const widths = getColumnWidths();

  return (
    <GeneralTableBox
      sx={{
        '& table': {
          tableLayout: 'fixed',
          width: '100%',
          minWidth: '0',
        },
        '& th, & td': {
          whiteSpace: 'normal',
        },
      }}
    >
      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            <HeadTableCell sx={{ width: { xs: '20%', md: '5%' } }}>Orden</HeadTableCell>
            <HeadTableCell sx={{ width: { xs: '60%', md: '25%' } }}>Nombre</HeadTableCell>
            <HeadTableCell width="30%" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
              Descripción
            </HeadTableCell>
            {isQualityTemplate && (
              <HeadTableCell
                width={widths.phase}
                sx={{ display: { xs: 'none', md: 'table-cell' } }}
              >
                Fase
              </HeadTableCell>
            )}
            <HeadTableCell width="8%" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
              % Máx
            </HeadTableCell>
            <HeadTableCell width="8%" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
              % Por Def
            </HeadTableCell>
            {isQualityTemplate && (
              <HeadTableCell
                width={widths.severity}
                sx={{ display: { xs: 'none', md: 'table-cell' } }}
              >
                Severidad
              </HeadTableCell>
            )}
            <HeadTableCell sx={{ width: { xs: '20%', md: '5%' } }}>Acciones</HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tasks && tasks.length > 0 ? (
            tasks
              .sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0))
              .map((task) => (
                <TableRow key={task.uuid} hover>
                  <RowTableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        textAlign: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <DragIndicatorIcon
                        sx={{ cursor: 'grab', color: 'text.secondary' }}
                        fontSize="small"
                      />
                      <Chip label={task.sequence_order || 0} size="small" variant="outlined" />
                    </Box>
                  </RowTableCell>
                  <RowTableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    <strong>{task.name}</strong>
                  </RowTableCell>
                  <RowTableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {task.description || 'Sin descripción'}
                  </RowTableCell>
                  {isQualityTemplate && (
                    <RowTableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {task.phase ? PHASE_LABELS[task.phase] || task.phase : '-'}
                    </RowTableCell>
                  )}
                  <RowTableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {task.max_percentage || 0}%
                  </RowTableCell>
                  <RowTableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {task.default_percentage || 0}%
                  </RowTableCell>
                  {isQualityTemplate && (
                    <RowTableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Chip
                        label={SEVERITY_LABELS[task.severity] || task.severity}
                        color={SEVERITY_COLORS[task.severity] || 'default'}
                        size="small"
                      />
                    </RowTableCell>
                  )}
                  <RowTableCell>
                    <IconButton
                      size="small"
                      onClick={() => onEdit(task)}
                      title="Editar tarea"
                      sx={{ mr: 0.5 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(task)}
                      title="Eliminar tarea"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </RowTableCell>
                </TableRow>
              ))
          ) : (
            <NoDataSkeleton message="No hay tareas definidas para esta plantilla" />
          )}
        </TableBody>
      </Table>

      <ConfirmationDialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Tarea"
        message={`¿Está seguro que desea eliminar la tarea "${deleteDialog.taskInfo?.name}"?`}
        confirmText="Eliminar tarea"
      />
    </GeneralTableBox>
  );
};

export default TasksDataTable;
